import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Card, Button, Spin, Checkbox, message, Row, Col, Tag, Space } from 'antd';
import LayoutWrapper from '@iso/components/utility/layoutWrapper.js';
import Box from '@iso/components/utility/box';
import { sprayService } from '@iso/services';
import { sprayJobService } from '../../../services/sprayJob.service';
import { getSprayJobStatusLabel, getSprayJobTagColor } from '@iso/constants/sprayJobStates';
import MixRecipe from './MixRecipe';
import PPEChecklist from './PPEChecklist';
import useSprayRequiredSections from '../../../hooks/useSprayRequiredSections';

/**
 * Full-page Prepare step (replaces the JobPreparationModal).
 * Operator confirms the prep tasks + water quantity, then calls /prepare to
 * transition status to PREPARED.
 */
const SprayJobPrepare = () => {
  const { id } = useParams();
  const history = useHistory();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [ppeChecked, setPpeChecked] = useState(false);
  // Per-property toggle map (Agronomist Admin → Required Confirmations).
  const { sections: req } = useSprayRequiredSections(job?.property_id);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    sprayService
      .getDetail(id)
      .then((res) => {
        if (cancelled) return;
        setJob(res?.spray_job || null);
      })
      .catch((err) => {
        console.error('Failed to load spray job:', err);
        message.error('Failed to load spray job');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <LayoutWrapper>
        <div style={{ textAlign: 'center', margin: '60px 0' }}>
          <Spin size="large" />
        </div>
      </LayoutWrapper>
    );
  }
  if (!job) {
    return (
      <LayoutWrapper>
        <Box>Spray job not found.</Box>
      </LayoutWrapper>
    );
  }

  const prepareTasks = (job.tasks || []).filter((t) => t.mapping_status === 'to_be_started');
  const allTasksChecked =
    prepareTasks.length === 0 || selectedTasks.length === prepareTasks.length;
  // Water-quantity confirmation card was removed per stakeholder feedback —
  // the Mix Recipe already lists the tank-fill steps. Gate now only covers
  // prep tasks + PPE.
  const tasksGate = !req.preparation_tasks || allTasksChecked;
  const ppeGate = !req.ppe_preparation || ppeChecked;
  const canSubmit = tasksGate && ppeGate;

  const toggleTask = (taskName, checked) => {
    setSelectedTasks((prev) =>
      checked ? [...prev, taskName] : prev.filter((t) => t !== taskName)
    );
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await sprayJobService.prepare(id);
      if (res && res.code === '0000') {
        message.success('Spray job prepared');
        history.push('/spray');
      } else {
        message.error(res?.message || 'Failed to prepare spray job');
      }
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to prepare spray job');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LayoutWrapper>
      <Box style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <h2 style={{ margin: 0 }}>Prepare: {job.job_name}</h2>
            <Tag color={getSprayJobTagColor(job.status)} style={{ marginTop: 8 }}>
              {getSprayJobStatusLabel(job.status)}
            </Tag>
          </Col>
          <Col>
            <Space>
              <Button onClick={() => history.push('/spray')}>Cancel</Button>
              <Button
                type="primary"
                disabled={!canSubmit}
                loading={submitting}
                onClick={handleSubmit}
              >
                Mark Prepared
              </Button>
            </Space>
          </Col>
        </Row>
      </Box>

      {req.ppe_preparation && (
        <PPEChecklist job={job} phase="preparation" onChange={setPpeChecked} />
      )}

      <MixRecipe job={job} />

      {req.preparation_tasks && (
      <Card title="Preparation Tasks" style={{ marginBottom: 16 }}>
        {prepareTasks.length === 0 && (
          <span style={{ color: '#666' }}>No preparation tasks defined for this job.</span>
        )}
        {prepareTasks.map((task) => (
          <div key={task.task_name} style={{ marginBottom: 8 }}>
            <Checkbox
              checked={selectedTasks.includes(task.task_name)}
              onChange={(e) => toggleTask(task.task_name, e.target.checked)}
            >
              {task.task_name}
            </Checkbox>
          </div>
        ))}
      </Card>
      )}

    </LayoutWrapper>
  );
};

export default SprayJobPrepare;
